/**
 * @fileoverview Click-to-open modal showing the linked invoice preview and quick actions.
 * @module features/payments/components/PaymentInvoiceBadge
 */

import { type ReactElement, useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FileText, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Badge } from '@/shared/components/ui'
import { cn } from '@/lib/utils'
import { unlinkPaymentFromInvoice } from '@/features/invoices/api/invoicesService'
import { useInvoiceFile } from '@/features/invoices/hooks/useInvoiceFile'
import { usePaymentLinkedInvoices } from '../hooks/usePaymentLinkedInvoices'

interface PaymentInvoiceBadgeProps {
  paymentId: string
  onEditInvoice: (invoiceUuid: string) => Promise<void> | void
  className?: string
}

interface InvoicePreviewProps {
  invoiceUuid: string
  hasFile: boolean
}

function PreviewFrame({ children }: { children: ReactElement }): ReactElement {
  return (
    <div className="h-[78vh] min-h-[420px] w-full overflow-hidden rounded-lg border border-border/40 bg-muted/20">
      {children}
    </div>
  )
}

function InvoicePreview({ invoiceUuid, hasFile }: InvoicePreviewProps): ReactElement {
  const { data: blob, isLoading, isError } = useInvoiceFile(invoiceUuid, hasFile)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [mimeType, setMimeType] = useState<string>('')

  useEffect(() => {
    if (!blob) return
    const url = URL.createObjectURL(blob)
    // eslint-disable-next-line react-hooks/set-state-in-effect -- deriving an object URL from a fetched Blob requires both creation and revocation cleanup.
    setBlobUrl(url)
    setMimeType(blob.type)
    return () => URL.revokeObjectURL(url)
  }, [blob])

  if (!hasFile) {
    return (
      <PreviewFrame>
        <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
          <FileText className="size-10 opacity-40" />
          <span className="text-sm">No invoice file</span>
        </div>
      </PreviewFrame>
    )
  }

  if (isLoading || !blobUrl) {
    return (
      <PreviewFrame>
        <div className="flex h-full items-center justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </PreviewFrame>
    )
  }

  if (isError) {
    return (
      <PreviewFrame>
        <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
          <FileText className="size-10 opacity-40" />
          <span className="text-sm">Failed to load preview</span>
        </div>
      </PreviewFrame>
    )
  }

  if (mimeType === 'application/pdf') {
    return (
      <PreviewFrame>
        <iframe
          src={`${blobUrl}#toolbar=0&navpanes=0&view=FitH`}
          className="h-full w-full"
          title="Invoice preview"
        />
      </PreviewFrame>
    )
  }

  if (mimeType.startsWith('image/')) {
    return (
      <PreviewFrame>
        <div className="flex h-full items-center justify-center">
          <img
            src={blobUrl}
            alt="Invoice preview"
            className="max-h-full max-w-full object-contain"
          />
        </div>
      </PreviewFrame>
    )
  }

  return (
    <PreviewFrame>
      <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
        <FileText className="size-10 opacity-40" />
        <span className="text-sm">Preview not available</span>
      </div>
    </PreviewFrame>
  )
}

export default function PaymentInvoiceBadge({
  paymentId,
  onEditInvoice,
  className,
}: PaymentInvoiceBadgeProps): ReactElement {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const { data, isLoading, isError } = usePaymentLinkedInvoices(paymentId, { enabled: open })
  const firstInvoice = data?.[0]
  const remainingCount = Math.max(0, (data?.length ?? 0) - 1)

  const unlinkMutation = useMutation({
    mutationFn: async (invoiceUuid: string) => unlinkPaymentFromInvoice(invoiceUuid, paymentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['payments'] })
      void queryClient.invalidateQueries({ queryKey: ['payments', 'linked-invoices', paymentId] })
      void queryClient.invalidateQueries({ queryKey: ['invoices'] })
      void queryClient.invalidateQueries({ queryKey: ['indirect-costs'] })
      setOpen(false)
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : 'Failed to unlink invoice')
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onPointerDown={event => event.stopPropagation()}
          onClick={event => event.stopPropagation()}
          aria-label="Linked invoice"
        >
          <Badge
            variant="secondary"
            className={cn(
              'h-5 gap-1 px-1.5 text-[10px] text-muted-foreground/80 transition-colors hover:bg-muted-foreground/20',
              className,
            )}
          >
            Invoice
            {remainingCount > 0 ? <span>+{remainingCount}</span> : null}
          </Badge>
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-3xl gap-3 p-4">
        {isLoading ? (
          <>
            <DialogHeader className="pr-8">
              <DialogTitle>Loading invoice...</DialogTitle>
              <DialogDescription className="sr-only">
                Fetching the linked invoice
              </DialogDescription>
            </DialogHeader>
            <div className="flex h-[420px] items-center justify-center">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          </>
        ) : isError ? (
          <>
            <DialogHeader className="pr-8">
              <DialogTitle>Failed to load invoice</DialogTitle>
              <DialogDescription className="sr-only">
                The linked invoice could not be loaded.
              </DialogDescription>
            </DialogHeader>
            <p className="text-sm text-destructive">Failed to load linked invoice</p>
          </>
        ) : firstInvoice ? (
          <>
            <DialogHeader className="pr-8">
              <DialogTitle className="truncate" title={firstInvoice.counterparty}>
                {firstInvoice.counterparty}
              </DialogTitle>
              <DialogDescription className="truncate" title={firstInvoice.invoiceNumber}>
                {firstInvoice.invoiceNumber}
              </DialogDescription>
            </DialogHeader>
            <InvoicePreview
              invoiceUuid={firstInvoice.invoiceUuid}
              hasFile={firstInvoice.hasFile}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  void onEditInvoice(firstInvoice.invoiceUuid)
                  setOpen(false)
                }}
              >
                Edit
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={unlinkMutation.isPending}
                onClick={() => unlinkMutation.mutate(firstInvoice.invoiceUuid)}
              >
                {unlinkMutation.isPending ? 'Unlinking...' : 'Unlink'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader className="pr-8">
              <DialogTitle>No linked invoice</DialogTitle>
              <DialogDescription>
                This payment has no linked invoice.
              </DialogDescription>
            </DialogHeader>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
